import { LIBRARY_ITEM_TYPE_LABELS, QUICK_FILTER_LABELS, toDisplayString } from '../filters';
import { LibraryItemType, FundUtils } from 'venn-utils';
import { compact } from 'lodash';
import type { FiltersState } from 'venn-state';
import { parseMorningstarCategory } from '../filters/components/CategoryFilter';
import type { TaxStatusEnum } from 'venn-api';

const TAX_STATUS_PILL_LABELS: Record<TaxStatusEnum, string> = {
  TAXABLE: 'Taxable',
  TAX_DEFERRED: 'Tax-deferred',
  TAX_EXEMPT: 'Tax-exempt',
};

interface GetFilterPillsProps {
  filtersState: FiltersState;
  toggleParamItem: (key: string, item: string) => void;
  updateSearchParam: (key: string, value: string) => void;
  removeAdvancedQuery: (index: number) => void;
}

export interface FilterPillProps {
  key: string;
  label: string;
  value: string;
  icon: string;
  onClick(): void;
  tooltip?: string;
}

export const getFilterPills = ({
  filtersState,
  toggleParamItem,
  updateSearchParam,
  removeAdvancedQuery,
}: GetFilterPillsProps): FilterPillProps[] => {
  return compact([
    ...filtersState.quickFilters.map((value) => ({
      label: QUICK_FILTER_LABELS[value],
      value,
      onClick: () => toggleParamItem('filters', value),
      icon: 'filter',
      key: `filter-${value}`,
    })),
    filtersState.itemType &&
      filtersState.itemType !== LibraryItemType.ALL && {
        label: LIBRARY_ITEM_TYPE_LABELS[filtersState.itemType],
        value: filtersState.itemType,
        onClick: () => updateSearchParam('itemType', LibraryItemType.ALL),
        icon: 'filter',
        key: `itemType-${filtersState.itemType}`,
      },
    ...filtersState.tags.map((tag) => ({
      label: tag,
      value: tag,
      onClick: () => toggleParamItem('tags', tag),
      icon: 'tag',
      key: `tag-${tag}`,
    })),
    ...filtersState.currency.map((currency) => ({
      label: currency,
      value: currency,
      onClick: () => toggleParamItem('currency', currency),
      icon: 'filter',
      key: `currency-${currency}`,
    })),
    ...filtersState.assetTypes.map((assetType) => ({
      label: FundUtils.getAssetTypeName(assetType),
      value: assetType,
      onClick: () => toggleParamItem('assetTypes', assetType),
      icon: 'filter',
      key: `assetType-${assetType}`,
    })),
    ...filtersState.dataSource.map((dataSource) => ({
      label: dataSource,
      value: dataSource,
      onClick: () => toggleParamItem('dataSource', dataSource),
      icon: 'filter',
      key: `dataSource-${dataSource}`,
    })),
    ...filtersState.taxStatuses.map((taxStatus) => ({
      label: TAX_STATUS_PILL_LABELS[taxStatus],
      value: taxStatus,
      onClick: () => toggleParamItem('taxStatuses', taxStatus),
      icon: 'filter',
      key: `taxStatus-${taxStatus}`,
    })),
    ...filtersState.morningstarCategories.map((category) => {
      const { name, id } = parseMorningstarCategory(category);
      return {
        label: name,
        value: category,
        onClick: () => toggleParamItem('morningstarCategories', category),
        icon: 'star',
        key: `morningstarCategory-${category}`,
        tooltip: `Morningstar Category Code: ${id}`,
      };
    }),
    ...filtersState.advancedQueries.map((query, index) => ({
      label: toDisplayString(query),
      value: '',
      onClick: () => removeAdvancedQuery(index),
      icon: 'crosshairs',
      key: `query-${index}`,
    })),
  ]);
};
